import { useEffect, useState } from 'react';

import useRouter from 'app-migration/hooks/useRouter';

import { FundMethods } from 'stores/ui/registration/registration';
import analyticsProvider from 'utils/analytics/analyticsProvider';
import { fireImpactDeposit } from 'utils/analytics/providers/impact';
import { fireDepositedPostBacks, fireReferralDepositPostBack } from 'utils/analytics/providers/predictionNews';
import { toCentsInteger, toMoneyWithCentsIfCents } from 'utils/caseing';
import { pollTransferStatus } from 'utils/transferPolling';

import { useStores } from './use-stores';

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
// declare const tatari: any;

const useDeposit = () => {
    const router = useRouter();
    const { uiStore, traderStore } = useStores();
    const bankStore = traderStore.bank;
    const options = bankStore.accountOptions;
    const wireDialog = uiStore.wireDepositDialog;
    const depositDialog = uiStore.depositDialog;
    const registrationStore = uiStore.registration;
    const paymentStore = traderStore.payments;
    const userStore = traderStore.user;

    const [amount, setAmount] = useState<number>(0);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [bankId, setBankId] = useState<string>('');
    const [cardId, setCardId] = useState<string>('');
    const [invalidMessage, setInvalidMessage] = useState<string | undefined>(undefined);

    const depositFeeInDollars = 0;
    const limit = traderStore.bank.resolveAccount(bankId)?.limitInDollars;
    const noDepositsAvailable = traderStore.bank.inflightDepositsAvailable < 1;

    const isBankFundMethod = registrationStore.fundMethod === FundMethods.BANK;
    const isWireFundMethod = registrationStore.fundMethod === FundMethods.WIRE;
    const isDebitCardFundMethod = registrationStore.fundMethod === FundMethods.DEBIT_CARD;

    let transferFrom: string | undefined = undefined;

    // Bank deposit flow
    if (isBankFundMethod && !!bankId) {
        const bankAccount = bankStore.resolveAccount(bankId);
        transferFrom = bankAccount?.name;
    }

    // Initialize amount from dialog when it opens
    useEffect(() => {
        if (depositDialog.isOpen && depositDialog.amount > 0 && amount === 0) {
            setAmount(depositDialog.amount);
        }
    }, [depositDialog.isOpen, depositDialog.amount]);

    useEffect(
        () => {
            bankStore.loadDepositLimitInfo();
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [amount],
    );

    useEffect(
        () => {
            if (!limit) {
                bankStore.loadDepositLimitInfo();
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [limit],
    );

    useEffect(
        () => {
            if (bankId === '') {
                const firstValidOption = options.find((option) => {
                    const account = bankStore.linkedAccounts.get(option.value);
                    return !!account && !account.isBlocked;
                });
                setBankId(!!firstValidOption ? firstValidOption.value : '');
            }
            if (!paymentStore.loadedGetDebitCards) {
                paymentStore.loadDebitCards(userStore.getUserId);
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const isValidDeposit = (amountToValidate: number = amount, isSubmitting: boolean = false): boolean => {
        // Check document verification limit for ACH deposits
        const documentVerificationResult = uiStore.registration?.kyc?.documentVerificationResult;
        const isDocVerified = documentVerificationResult === 'approved';
        const docVerificationLimit = bankStore.maxDepositAmountWithoutDocVerification;
        const dailyDepositLimit =
            bankStore.dailyDepositCentsAvailable !== undefined ? bankStore.dailyDepositCentsAvailable / 100 : undefined;

        // Check if doc verification is required
        // If limit is 0, doc verification is always required
        // If limit > 0, check if amount exceeds it
        if (
            !isDocVerified &&
            docVerificationLimit !== undefined &&
            (docVerificationLimit === 0 || amountToValidate > docVerificationLimit)
        ) {
            if (isSubmitting) {
                // Store deposit info for after verification
                sessionStorage.setItem('pendingBankDeposit', 'true');
                sessionStorage.setItem('pendingBankDepositAmount', amountToValidate.toString());
                // Close deposit dialog before redirecting
                depositDialog.close();
                // Redirect to document verification
                router.push('/sign-up/identity');
                return false;
            }
            // During typing, don't show any error about doc verification - let them continue
            // Only show errors for other limits below
        }

        if (!bankStore.numberOfActiveAccounts) {
            setInvalidMessage(
                'In order to deposit funds through ACH you must register your bank accounts on the account section and wait for it to link. Wire is also available.',
            );
            return false;
        }

        if (noDepositsAvailable) {
            setInvalidMessage(
                `You've reached the in-flight deposit limit for now. They usually take 1 to 4 business days to complete. Don't want to wait? Use wire transfer instead.`,
            );
            return false;
        }

        // Check against daily deposit limit across all rails
        if (dailyDepositLimit !== undefined) {
            if (dailyDepositLimit === 0) {
                setInvalidMessage(`You've hit your daily deposit limit`);
                return false;
            }
            if (amountToValidate > dailyDepositLimit) {
                setInvalidMessage(
                    `You can deposit up to ${toMoneyWithCentsIfCents(dailyDepositLimit)} today across all methods`,
                );
                return false;
            }
        }

        if (!!limit && limit === 0) {
            setInvalidMessage(`You've hit your limit for bank transfer today`);
            return false;
        }

        if (!!limit && amountToValidate > limit) {
            setInvalidMessage(`You can deposit up to ${toMoneyWithCentsIfCents(limit)} with bank transfer today`);
            return false;
        }

        if (Number(amountToValidate) < 10) {
            setInvalidMessage('$10 minimum deposit');
            return false;
        }

        return true;
    };

    const review = (amountToValidate: number = amount): void => {
        if (!isValidDeposit(amountToValidate, true)) {
            return;
        }

        if (options.length === 1 && !options[0].disabled) {
            setBankId(options[0].value);
        }

        if (bankId === '' && options.length !== 1) {
            return;
        }

        depositDialog.setAmount(amount);
        depositDialog.setFee(0);
        depositDialog.setStep('review');
    };

    const submit = (): void => {
        userStore.metrics.recordDepositMetric({
            step: 'submitBankDeposit',
        });

        if (submitting) {
            return;
        }

        userStore.metrics.recordDepositMetric({
            step: 'submitBankDepositInit',
        });

        setSubmitting(true);
        depositDialog.setDeposit(undefined);
        bankStore.createTransfer(
            bankId,
            toCentsInteger(Number(amount + depositFeeInDollars)),
            true,
            (transfer) => {
                userStore.metrics.recordDepositMetric({
                    step: 'submitBankDepositSuccess',
                });

                depositDialog.setAmount(amount);
                depositDialog.loading = true;

                const { timeoutId } = pollTransferStatus({
                    bankStore,
                    userId: userStore.getUserId,
                    transferId: transfer?.id,
                    isDeposit: true,
                    dialog: depositDialog,
                    onSuccess: (successTransfer) => {
                        clearTimeout(timeoutId);
                        depositDialog.setStep('initiated');
                        depositDialog.setFee(successTransfer.feeInDollars);
                        depositDialog.setDeposit(transfer);
                        depositDialog.loading = false;
                        setSubmitting(false);
                    },
                    onFailure: () => {
                        clearTimeout(timeoutId);
                        setSubmitting(false);
                    },
                    onTimeout: () => {
                        depositDialog.setStep('initiated');
                        depositDialog.setDeposit(transfer);
                        depositDialog.loading = false;
                        setSubmitting(false);
                    },
                });

                analyticsProvider.sendEvent('DepositInitiated', {
                    page: window.location.pathname,
                    succeeded: true,
                    amount: amount,
                });
                fireDepositedPostBacks(bankStore.depositsCount, amount, uiStore.getPredictionNewsUserId());
                // Also fire referral postback if user has a referral code
                const referralCode = traderStore.user.referrals?.referralInfo?.referral_code;
                if (referralCode) {
                    fireReferralDepositPostBack(referralCode);
                }
                // tatari.track('Deposited', { amount: amount, userId: traderStore.user.getUserId });

                // Track deposit with Impact.com
                if (transfer && transfer.id) {
                    fireImpactDeposit(
                        traderStore.user.getUserId,
                        amount,
                        transfer.id,
                        undefined, // promo code if available
                    ).catch((err) => {
                        console.error('Failed to track Impact deposit:', err);
                    });
                }
            },
            (errTitle, errMessage, errCode) => {
                userStore.metrics.recordDepositMetric({
                    step: 'submitBankDepositError',
                });
                uiStore.heapTrack('DepositFailed', {
                    page: window.location.pathname,
                    succeeded: false,
                    amount: amount,
                });
                setSubmitting(false);

                if (errCode === 'insufficient_balance') {
                    // Display error on error top message on pre-review step
                    depositDialog.setStep('prereview');
                    setInvalidMessage(`${errTitle}. ${errMessage}.`);
                } else {
                    // Display error on fail component on review step
                    depositDialog.setStep('failed');
                    depositDialog.setErrorTitle(errTitle);
                    depositDialog.setErrorMessage(errMessage);
                    depositDialog.setErrorCode(errCode);
                }
            },
        );
    };

    const edit = (): void => {
        depositDialog.setStep('prereview');
    };

    const reset = (): void => {
        setAmount(0);
        setSubmitting(false);
        depositDialog.setInitialStep();
        depositDialog.setErrorMessage(undefined);
    };

    const tryAgain = (): void => {
        depositDialog.setStep('review');
        depositDialog.setErrorMessage(undefined);
    };

    const openWireDialog = (): void => {
        uiStore.heapTrack('WireAttempt', {
            page: window.location.pathname,
            email: traderStore.user.user.email,
        });
        depositDialog.close();
        wireDialog.open();
    };

    const title =
        depositDialog.step === 'kyc'
            ? "We'll need a quick photo of your ID for verification"
            : depositDialog.step === 'billing-address'
            ? 'Billing address'
            : depositDialog.step === 'prereview'
            ? isBankFundMethod
                ? 'Deposit to Kalshi'
                : isWireFundMethod
                ? 'Wire transfer instructions'
                : isDebitCardFundMethod
                ? ''
                : ''
            : depositDialog.step === 'debit-card'
            ? 'Enter debit card info'
            : depositDialog.step === 'review'
            ? `${toMoneyWithCentsIfCents(amount)} deposit review`
            : '';

    return {
        amount,
        setAmount,
        submitting,
        setSubmitting,
        bankId,
        setBankId,
        cardId,
        setCardId,
        invalidMessage,
        setInvalidMessage,
        title,
        isValidDeposit,
        review,
        submit,
        edit,
        reset,
        tryAgain,
        openWireDialog,
        transferFrom,
    };
};

export default useDeposit;
